import { HttpErrorResponse, HttpEvent, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { catchError, EMPTY, Observable, of, retry, switchMap, throwError, timer } from 'rxjs';

import { sessionRoutes } from '../constants/router';
import { AccessService } from '../services/access/access.service';

const AUTHORIZATION = 'Authorization';
const SERVER_ERROR_RETRIES = 2;
const SERVER_ERROR_RETRY_DELAY_MS = 2000;
const GENERIC_ERROR_MESSAGE = 'Something went wrong. Please try again.';

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const accessService = inject(AccessService);
  const toastr = inject(ToastrService);

  function send(request: HttpRequest<unknown>, refreshed: boolean): Observable<HttpEvent<unknown>> {
    return next(request).pipe(
      retry({
        count: SERVER_ERROR_RETRIES,
        delay: (error: unknown) =>
          isServerError(error) ? timer(SERVER_ERROR_RETRY_DELAY_MS) : throwError(() => error),
      }),
      catchError((error: unknown) => {
        if (!(error instanceof HttpErrorResponse)) {
          return throwError(() => error);
        }

        if (error.status === 401) {
          return onUnauthorized(request, error, refreshed);
        }

        if (error.status >= 400 && error.status < 500) {
          toastr.error(toMessage(error));
        }

        return throwError(() => error);
      })
    );
  }

  function onUnauthorized(
    request: HttpRequest<unknown>,
    error: HttpErrorResponse,
    refreshed: boolean
  ): Observable<HttpEvent<unknown>> {
    if (!accessService.isLoggedIn() || isSessionRoute(request.url)) {
      return throwError(() => error);
    }

    if (refreshed) {
      accessService.logout();
      return EMPTY;
    }

    // A parallel request may already have refreshed the token this one was sent with.
    const current = accessService.getJWT();
    const token$ =
      current && bearerToken(request) !== current ? of(current) : accessService.refreshSession();

    return token$.pipe(
      catchError(() => {
        accessService.logout();
        return EMPTY;
      }),
      switchMap(token =>
        send(request.clone({ setHeaders: { [AUTHORIZATION]: `Bearer ${token}` } }), true)
      )
    );
  }

  return send(req, false);
};

function isServerError(error: unknown): boolean {
  return error instanceof HttpErrorResponse && error.status >= 500;
}

function isSessionRoute(url: string): boolean {
  return sessionRoutes.some(route => url.includes(route));
}

function bearerToken(request: HttpRequest<unknown>): string | null {
  return request.headers.get(AUTHORIZATION)?.replace(/^Bearer /, '') ?? null;
}

function toMessage(error: HttpErrorResponse): string {
  const body: unknown = error.error;

  if (typeof body === 'string' && body.trim().length > 0) {
    return body;
  }

  if (
    typeof body === 'object' &&
    body !== null &&
    'message' in body &&
    typeof body.message === 'string' &&
    body.message.length > 0
  ) {
    return body.message;
  }

  return error.message || GENERIC_ERROR_MESSAGE;
}
